import { z } from 'zod';

/* ─────────────────────────────────────────────────────────────────────
   Publicação: a peça aprovada vira um pacote estático imutável, é
   versionada no GitHub do cliente e vai para o ar no EasyPanel (ou na
   hospedagem de prévia do próprio ADS). Tudo passo a passo, visível.
   ───────────────────────────────────────────────────────────────────── */

export const siteSlugSchema = z.string().trim().min(2, 'pelo menos 2 caracteres').max(40, 'no máximo 40 caracteres')
  .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'use letras minúsculas, números e hífens (sem hífen nas pontas)');

const envName = z.string().regex(/^[A-Z][A-Z0-9_]{2,63}$/, 'nome de variável de ambiente, ex.: GITHUB_TOKEN_AURORA');

export const githubTargetSchema = z.object({
  repo: z.string().trim().regex(/^[\w.-]+\/[\w.-]+$/, 'use o formato dono/repositório'),
  branch: z.string().trim().min(1).max(100).regex(/^[\w./-]+$/, 'nome de branch inválido').default('main'),
  /** pasta dentro do repositório; cada site fica em <basePath>/<slug>/ */
  basePath: z.string().trim().max(100).regex(/^$|^[\w-]+(\/[\w-]+)*$/, 'pasta relativa, ex.: sites').default('sites'),
  /** token: fine-grained PAT numa variável de ambiente · app: GitHub App instalada no repositório */
  auth: z.enum(['token', 'app']).default('token'),
  /** nome da variável de ambiente com o token (auth = token). Vazio usa GITHUB_TOKEN. */
  secretRef: envName.nullable().default(null),
  /** id da instalação da GitHub App (auth = app) */
  installationId: z.string().trim().regex(/^\d{1,20}$/, 'número da instalação').nullable().default(null),
});
export type GithubTarget = z.infer<typeof githubTargetSchema>;

export const easypanelTargetSchema = z.object({
  /** projeto do EasyPanel onde cada site vira um serviço */
  project: z.string().trim().regex(/^[a-z0-9][a-z0-9_-]{0,39}$/, 'letras minúsculas, números, - e _').default('sites'),
  /** domínio de cada site; {slug} é trocado pelo endereço escolhido */
  domainTemplate: z.string().trim().toLowerCase().max(200)
    .regex(/^([a-z0-9-]+|\{slug\})(\.([a-z0-9-]+|\{slug\}))+$/, 'um domínio, ex.: {slug}.sites.suaagencia.com.br')
    .refine(v => v.includes('{slug}'), 'inclua {slug} no domínio'),
  /** bundle: o EasyPanel baixa o pacote do ADS · github: o EasyPanel constrói a partir do repositório */
  mode: z.enum(['bundle', 'github']).default('bundle'),
});
export type EasypanelTarget = z.infer<typeof easypanelTargetSchema>;

export const publishSettingsSchema = z.object({
  github: githubTargetSchema.nullable(),
  easypanel: easypanelTargetSchema.nullable(),
});
export type PublishSettingsInput = z.infer<typeof publishSettingsSchema>;

export interface TargetStatus { ready: boolean; problem: string | null }

export interface PublishSettingsDto {
  github: (GithubTarget & TargetStatus) | null;
  easypanel: (EasypanelTarget & TargetStatus) | null;
  /** o que esta instalação do ADS sabe fazer, independentemente da organização */
  server: {
    easypanel: boolean;
    easypanelUrl: string | null;
    publicUrl: string | null;
    githubApp: boolean;
    /** o próprio ADS serve os sites em /s/<slug>/ quando o EasyPanel não está configurado */
    adsHosting: boolean;
    defaultDomain: string | null;
    defaultProject: string;
  };
}

export const publishRequestSchema = z.object({
  slug: siteSlugSchema,
  github: z.boolean().default(true),
  easypanel: z.boolean().default(true),
});
export type PublishRequest = z.infer<typeof publishRequestSchema>;

export type PublishStepKey = 'bundle' | 'github' | 'host' | 'deploy' | 'verify';
export type PublishStepStatus = 'todo' | 'running' | 'done' | 'skipped' | 'warn' | 'failed';

export interface PublishStep {
  key: PublishStepKey;
  label: string;
  status: PublishStepStatus;
  detail: string | null;
  /** link útil do passo: commit, serviço no painel, site */
  href: string | null;
  startedAt: string | null;
  finishedAt: string | null;
}

export interface PublicationDto {
  id: string;
  artifactId: string;
  slug: string;
  status: 'running' | 'done' | 'failed';
  host: 'easypanel' | 'ads' | null;
  steps: PublishStep[];
  url: string | null;
  commitSha: string | null;
  commitUrl: string | null;
  bundleId: string;
  /** a versão da peça que foi para o pacote (null: publicada antes de as versões existirem) */
  revision: number | null;
  fileCount: number;
  error: string | null;
  createdBy: string | null;
  createdAt: string;
  finishedAt: string | null;
}

export interface SiteDto {
  id: string;
  slug: string;
  title: string;
  tool: string;
  url: string | null;
  artifactId: string | null;
  publication: { id: string; status: PublicationDto['status']; createdAt: string; commitUrl: string | null; revision: number | null } | null;
  updatedAt: string;
}

/** Sugestão de endereço a partir do nome da organização e da peça. */
export function suggestSlug(org: string, extra = ''): string {
  const s = `${org} ${extra}`.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
    .replace(/\b(escola|colegio|instituto|de|da|do|das|dos|e|the)\b/g, ' ')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').replace(/-{2,}/g, '-');
  return (s || 'site').slice(0, 40).replace(/-+$/, '');
}
